import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { shareReplay } from "rxjs/operators";
import { GetChatMessageListDto } from "../models/common/GetChatMessageListDto";
import { SendChatMessageRequestDto } from "../models/common/SendChatMessageRequestDto";
import { GenericRequestDto } from "../models/generic-request.dto";
import { GenericResponseDto } from "../models/generic-response.dto";
import { EmailTemplates } from "../models/orders/emailTemplates";
import { PreSalesProjectWorkflowList } from "../models/orders/preSalesProjectWorkflowList";
import { AddProjectWorkflowLineNotesDto } from "../models/project-workflow/addProjectWorkflowLineNotesDto";
import { AddWorkflowHeaderListDto } from "../models/project-workflow/addWorkflowHeaderListDto";
import { AddWorkflowProposalsDto } from "../models/project-workflow/addWorkflowProposalsDto";
import { AddWorkflowTrackingRequestDto } from "../models/project-workflow/addWorkflowTrackingRequestDto";
import { CreateWorkflowHeaderResponseDto } from "../models/project-workflow/createWorkflowHeaderResponseDto";
import { DeleteProjectWorkflowLinesDto } from "../models/project-workflow/deleteProjectWorkflowLinesDto";
import { DeleteWorkflowLineTrackingDto } from "../models/project-workflow/deleteWorkflowLineTracking";
import { NonAddedSalesOrderLineDto } from "../models/project-workflow/getNonAddedProjectWorkflowLineDto";
import { GetProductionWorkflowErrorLogsDto } from "../models/project-workflow/getProductionWorkflowErrorLogsDto";
import { ProjectWorkflowTrackingNumberDetailsDto } from "../models/project-workflow/GetProjectWorkflowTrackingDetailsDto";
import { AddLinesToProjectWorkflowDto } from "../models/project-workflow/postAddLinesToProjectWorkflowDto";
import { ProductionWorkflowDeliveryStatusDto } from "../models/project-workflow/productionWorkflowDeliveryStatusDto";
import { ProductionWorkflowDetails } from "../models/project-workflow/productionWorkflowDetails";
import { ProductionWorkflowLineDto } from "../models/project-workflow/productionWorkflowLineDto";
import { ProductionWorkflowLineTrackingDto } from "../models/project-workflow/productionWorkflowLineTrackingDto";
import { ProductionWorkflowListDto } from "../models/project-workflow/productionWorkflowListDto";
import { ProductionWorkflowOwnerReassignDto } from "../models/project-workflow/productionWorkflowOwnerReassignDto";
import { ProductionWorkflowOwnersDto } from "../models/project-workflow/productionWorkflowOwnersDto";
import { ProductionWorkflowReassignLogDto } from "../models/project-workflow/productionWorkflowReassignLogDto";
import { ProjectFileDto } from "../models/project-workflow/projectFileDto";
import { ProjectWorkflowLineDto } from "../models/project-workflow/projectWorkflowLineDto";
import { ProjectWorkflowReAssignEmailDto } from "../models/project-workflow/projectWorkflowReAssignEmailDto";
import { ProjectWorkflowTrackingHistoryDto } from "../models/project-workflow/projectWorkflowTrackingHistoryDto";
import { SalesOrderDetailDto } from "../models/project-workflow/salesOrderDetailDto";
import { TrackingStatusResponseDto } from "../models/project-workflow/trackingStatusResponseDto";
import { UpdateManualTrackingStatusDto } from "../models/project-workflow/updateManualTrackingStatusRequestDto";
import { UpdateNotificationEmailDto } from "../models/project-workflow/updateNotificationEmailDto";
import { UpdateNotificationSettingsDto } from "../models/project-workflow/updateNotificationSettingsDto";
import { UpdateProjectWorkflowFlagDto } from "../models/project-workflow/updateProjectWorkflowFlagDto";
import { UpdateProjectWorkflowIhdDto } from "../models/project-workflow/updateProjectWorkflowIhdDto";
import { UpdateProjectWorkflowSortingDto } from "../models/project-workflow/updateProjectWorkflowSortingDto";
import { UpdateWorkflowHeaderRequestDto } from "../models/project-workflow/updateWorkflowHeaderRequestDto";
import { UpdateWorkflowHeaderResponseDto } from "../models/project-workflow/updateWorkflowHeaderResponseDto";
import { WorkflowDeliveryStatusAdditionalFilters } from "../models/project-workflow/workflowDeliveryStatusAdditionalFilters";
import { WorkflowNotificationSettings } from "../models/project-workflow/workflownotificationSettingDto";
import { WorkflowStatusDto } from "../models/project-workflow/workflowStatusDto";
import { WorkflowUpdatesDto } from "../models/project-workflow/workflowUpdatesDto";
import { WorkflowUpdatesPinRequestDto } from "../models/project-workflow/workflowUpdatesPinRequestDto";
import { WorkflowUpdatesResponseDto } from "../models/project-workflow/workflowUpdatesResponseDto";
import { FileUploadDto } from "../models/shared/fileUploadDto";
import { environment } from "../../../environments/environment";
import { CreateWorkflowHeaderRequestDto } from "../models/project-workflow/createWorkflowHeaderRequestDto";
import { CreateSampleWorkflowHeaderRequestDto } from "../models/project-workflow/createSampleWorkflowHeaderRequestDto";
import { GetAllTaskResponse } from "../models/task-management/getAllTaskResponseDto";
import { RenameProjectFileRequestDto } from "../models/project-workflow/renameProjectFileRequestDto";
import { MarkAsArchiveRequestDto } from "../models/project-workflow/markAsArchiveRequestDto";
import { AddProjectWorkFlowLinesTemplatesRequestDto } from "../models/project-workflow/addProjectWorkFlowLinesTemplatesRequestDto";
import { ProjectWorkFlowLinesTemplates } from "../models/project-workflow/projectWorkFlowLinesTemplates";

@Injectable({
  providedIn: "root",
})
export class ProjectWorkflowService {
  private productionWorkflowBaseURL = environment.baseUrl + "/ProjectWorkflow";
  private commonBaseURL = environment.baseUrl + "/Common";
  private fileManagementBaseURL = environment.baseUrl + "/fileManagement";
  private taskManagementBaseURL = environment.baseUrl + "/TaskManagement";
  private chatBoxBaseURL = environment.baseUrl + "/ChatBox";
  private salesOrderManagmentBaseURL =
    environment.baseUrl + "/SalesOrderManagement";

  // Cache for workflow status list
  private workflowStatusListCache$?: Observable<
    GenericResponseDto<WorkflowStatusDto[]>
  >;

  constructor(private _http: HttpClient) {}

  addWorkflowHeaderData(
    payload: CreateWorkflowHeaderRequestDto
  ): Observable<GenericResponseDto<CreateWorkflowHeaderResponseDto>> {
    return this._http.post<GenericResponseDto<CreateWorkflowHeaderResponseDto>>(
      this.productionWorkflowBaseURL + "/AddWorkflowHeader",
      payload
    );
  }

  updateWorkflowHeaderData(
    payload: UpdateWorkflowHeaderRequestDto
  ): Observable<GenericResponseDto<UpdateWorkflowHeaderResponseDto>> {
    return this._http.post<GenericResponseDto<UpdateWorkflowHeaderResponseDto>>(
      this.productionWorkflowBaseURL + "/UpdateWorkflowHeader",
      payload
    );
  }

  getSaleOrderDetails(
    saleOrderId: ""
  ): Observable<GenericResponseDto<SalesOrderDetailDto>> {
    return this._http.get<GenericResponseDto<SalesOrderDetailDto>>(
      this.productionWorkflowBaseURL +
        `/GetSaleOrderDetails?saleOrderId=${saleOrderId}`
    );
  }

  getProductionWorkflowDetails(
    SaleOrderId: string,
    WorkflowId: number
  ): Observable<GenericResponseDto<ProductionWorkflowDetails>> {
    return this._http.get<GenericResponseDto<ProductionWorkflowDetails>>(
      this.productionWorkflowBaseURL +
        `/GetProductionWorkflowDetails?SaleOrderId=${SaleOrderId}&WorkflowId=${WorkflowId}`
    );
  }

  postWorkflowUpdates(
    payload: WorkflowUpdatesDto
  ): Observable<GenericResponseDto<WorkflowUpdatesResponseDto>> {
    return this._http.post<GenericResponseDto<WorkflowUpdatesResponseDto>>(
      this.productionWorkflowBaseURL + "/AddProductionWorkflowUpdate",
      payload
    );
  }

  getWorkflowUpdatesList(
    workflowId: number
  ): Observable<GenericResponseDto<WorkflowUpdatesResponseDto[]>> {
    return this._http.get<GenericResponseDto<WorkflowUpdatesResponseDto[]>>(
      this.productionWorkflowBaseURL +
        `/GetProductionWorkflowUpdateList?ProjectWorkflowId=${workflowId}`
    );
  }

  getWorkFlowTrackingExcelTemplate(): Observable<Blob> {
    return this._http.get(
      this.productionWorkflowBaseURL + `/GetWorkFlowTrackingExcelTemplate`,
      {
        responseType: "blob",
      }
    );
  }

  postWorkflowLineTrackingList(
    payload: AddWorkflowTrackingRequestDto
  ): Observable<GenericResponseDto<string>> {
    return this._http.post<GenericResponseDto<string>>(
      this.productionWorkflowBaseURL + "/PostWorkflowTrackingsCreateExcel",
      payload
    );
  }

  getWorkflowLineTrackingList(
    workflowId: number,
    lineRecIdD365: number
  ): Observable<GenericResponseDto<ProductionWorkflowLineTrackingDto[]>> {
    return this._http.get<
      GenericResponseDto<ProductionWorkflowLineTrackingDto[]>
    >(
      this.productionWorkflowBaseURL +
        `/GetWorkflowLineTrackingList?WorkflowId=${workflowId}&LineRecIdD365=${lineRecIdD365}`
    );
  }

  getWorkflowLineItems(
    workflowId: number,
    salesOrderIdD365: string,
    isShowAllLines: boolean
  ): Observable<GenericResponseDto<ProductionWorkflowLineDto[]>> {
    return this._http.get<GenericResponseDto<ProductionWorkflowLineDto[]>>(
      this.productionWorkflowBaseURL +
        `/GetProductionWorkflowLineItemList?WorkflowId=${workflowId}&SalesOrderIdD365=${salesOrderIdD365}&IsShowAllLines=${isShowAllLines}`
    );
  }

  getProjectWorkflowSummaryDocument(
    salesOrderId: string,
    workflowId: number,
    watermarkType = ""
  ): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      this.productionWorkflowBaseURL +
        `/ProductionWorkflowSummaryDocument?workflowId=${workflowId}&WatermarkType=${watermarkType}`
    );
  }

  getEmailTemplatesByType(
    type: string
  ): Observable<GenericResponseDto<EmailTemplates[]>> {
    return this._http.get<GenericResponseDto<EmailTemplates[]>>(
      `${this.commonBaseURL}/GetEmailTemplatesByType?type=${type}`
    );
  }

  getWorkflowEmailAttachmentUrl(
    fileObj: FileUploadDto
  ): Observable<GenericResponseDto<string>> {
    return this._http.post<GenericResponseDto<string>>(
      this.fileManagementBaseURL + "/upload",
      fileObj
    );
  }

  deleteWorkflowLineTracking(
    payload: DeleteWorkflowLineTrackingDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/DeleteWorkflowLineTracking",
      payload
    );
  }

  reassignWorkflow(
    payload: ProductionWorkflowOwnerReassignDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/ReassignProductionWorkflowOwner",
      payload
    );
  }

  getReassignedWorkflowLogs(
    workflowId: number
  ): Observable<GenericResponseDto<ProductionWorkflowReassignLogDto[]>> {
    return this._http.get<
      GenericResponseDto<ProductionWorkflowReassignLogDto[]>
    >(
      this.productionWorkflowBaseURL +
        `/GetReassignWorkflowOwnerLogs?WorkflowId=${workflowId}`
    );
  }

  getAllWorkflows(
    request: GenericRequestDto
  ): Observable<GenericResponseDto<ProductionWorkflowListDto[]>> {
    return this._http.post<GenericResponseDto<ProductionWorkflowListDto[]>>(
      this.productionWorkflowBaseURL + "/GetProductionWorkflowList",
      request
    );
  }

  sendChatMessage(
    messageRequest: SendChatMessageRequestDto
  ): Observable<GenericResponseDto<string>> {
    return this._http.post<GenericResponseDto<string>>(
      this.chatBoxBaseURL + "/SendMessage",
      messageRequest
    );
  }

  getChatMessageList(
    workflowId: number,
    moduleName: string
  ): Observable<GenericResponseDto<GetChatMessageListDto[]>> {
    return this._http.get<GenericResponseDto<GetChatMessageListDto[]>>(
      this.chatBoxBaseURL +
        `/GetMessagesList?moduleId=${workflowId}&moduleName=${moduleName}`
    );
  }

  getWorkflowOwnersList(
    workflowId: number
  ): Observable<GenericResponseDto<ProductionWorkflowOwnersDto[]>> {
    return this._http.get<GenericResponseDto<ProductionWorkflowOwnersDto[]>>(
      this.productionWorkflowBaseURL +
        `/GetProductionWorkflowOwnersList?WorkflowId=${workflowId}`
    );
  }

  getWorkflowStatusList(): Observable<GenericResponseDto<WorkflowStatusDto[]>> {
    if (!this.workflowStatusListCache$) {
      this.workflowStatusListCache$ = this._http
        .get<
          GenericResponseDto<WorkflowStatusDto[]>
        >(this.productionWorkflowBaseURL + "/GetProductionWorkflowStatusList")
        .pipe(shareReplay(1));
    }
    return this.workflowStatusListCache$;
  }

  // Method to clear the cache if needed (e.g., when data changes)
  clearWorkflowStatusListCache(): void {
    this.workflowStatusListCache$ = undefined;
  }

  checkShipmentCycleAllowed(
    salesOrderId: string
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.get<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL +
        `/CheckShipmentCycleIsAllowed?SalesOrderId=${salesOrderId}`
    );
  }

  getSalesOrderNumberList(): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      this.productionWorkflowBaseURL + `/GetSalesOrderNumberList`
    );
  }

  getTrackingDetailsByTrackingNumber(
    trackingNumber: string,
    lineRecIdD365: number
  ): Observable<GenericResponseDto<ProjectWorkflowTrackingNumberDetailsDto>> {
    return this._http.get<
      GenericResponseDto<ProjectWorkflowTrackingNumberDetailsDto>
    >(
      this.productionWorkflowBaseURL +
        `/GetFedexTrackingDetails?TrackingNumber=${trackingNumber}&LineRecIdD365=${lineRecIdD365}`
    );
  }

  getTrackingHistoryByTrackingNumber(
    trackingNumber: string,
    lineRecIdD365: number
  ): Observable<GenericResponseDto<ProjectWorkflowTrackingHistoryDto[]>> {
    return this._http.get<
      GenericResponseDto<ProjectWorkflowTrackingHistoryDto[]>
    >(
      this.productionWorkflowBaseURL +
        `/GetFedexTrackingDetailsHistory?TrackingNumber=${trackingNumber}&LineRecIdD365=${lineRecIdD365}`
    );
  }

  getWorkflowTrackingStatus(
    workflowId: number
  ): Observable<GenericResponseDto<TrackingStatusResponseDto>> {
    return this._http.get<GenericResponseDto<TrackingStatusResponseDto>>(
      this.productionWorkflowBaseURL +
        `/GetWorkflowTrackingStatus?workflowId=${workflowId}`
    );
  }

  getWorkflowNotificationSettings(
    workflowId: number,
    isGlobal: boolean,
    lineRecId: number,
    isLines: boolean
  ): Observable<GenericResponseDto<WorkflowNotificationSettings[]>> {
    return this._http.get<GenericResponseDto<WorkflowNotificationSettings[]>>(
      this.productionWorkflowBaseURL +
        `/GetWorkflowNotificationSettings?workflowId=${workflowId}&IsGlobal=${isGlobal.toString()}&LineRecId=${lineRecId}&IsLines=${isLines}`
    );
  }

  updateWorkflowNotificationSettings(
    request: UpdateNotificationSettingsDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/UpdateWorkflowNotificationSettings",
      request
    );
  }

  updateWorkflowEmailNotificationSetting(
    request: UpdateNotificationEmailDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL +
        "/UpdateWorkflowNotificationEmailSettings",
      request
    );
  }

  addSampleWorkflowHeaderData(
    request: CreateSampleWorkflowHeaderRequestDto
  ): Observable<GenericResponseDto<CreateWorkflowHeaderResponseDto>> {
    return this._http.post<GenericResponseDto<CreateWorkflowHeaderResponseDto>>(
      this.productionWorkflowBaseURL + "/AddSampleWorkflowHeader",
      request
    );
  }

  getWorkFlowHeaderCreateExcelTemplate(): Observable<Blob> {
    return this._http.get(
      this.productionWorkflowBaseURL + `/GetCreateWorkFlowHeaderExcelTemplate`,
      {
        responseType: "blob",
      }
    );
  }

  postWorkflowHeaderList(
    payload: AddWorkflowHeaderListDto[]
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/AddProjectWorkflowHeadersList",
      payload
    );
  }

  updateProjectWorkflowLines(
    payload: ProjectWorkflowLineDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/UpdateProjectWorkflowLine",
      payload
    );
  }

  projectWorkflowReAssignEmail(
    payload: ProjectWorkflowReAssignEmailDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/ProjectWorkflowReAssignEmail",
      payload
    );
  }

  getWorkflowDeliveryStatusList(
    request: WorkflowDeliveryStatusAdditionalFilters
  ): Observable<GenericResponseDto<ProductionWorkflowDeliveryStatusDto[]>> {
    return this._http.post<
      GenericResponseDto<ProductionWorkflowDeliveryStatusDto[]>
    >(
      this.productionWorkflowBaseURL + "/GetWorkflowDeliveryStatusList",
      request
    );
  }

  deleteProjectWorkflowLines(
    payload: DeleteProjectWorkflowLinesDto
  ): Observable<GenericResponseDto<string>> {
    return this._http.post<GenericResponseDto<string>>(
      this.productionWorkflowBaseURL + "/DeleteProjectWorkflowLines",
      payload
    );
  }

  getProductionWorkflowErrorLogs(
    workflowId: number
  ): Observable<GenericResponseDto<GetProductionWorkflowErrorLogsDto[]>> {
    return this._http.get<
      GenericResponseDto<GetProductionWorkflowErrorLogsDto[]>
    >(
      this.productionWorkflowBaseURL +
        `/GetProductionWorkflowErrorLogs?WorkflowId=${workflowId}`
    );
  }

  getTrackingSheetExcelTemplate(saleOrderId: string, workflowId: number) {
    return this._http.get(
      this.productionWorkflowBaseURL +
        `/GetTrackingSheetExcelTemplate?SaleOrderId=${saleOrderId}&workflowId=${workflowId}`,
      {
        responseType: "blob",
      }
    );
  }

  getQuotesList(): Observable<GenericResponseDto<string[]>> {
    return this._http.get<GenericResponseDto<string[]>>(
      this.productionWorkflowBaseURL + `/GetQuotes`
    );
  }

  addWorkflowProposals(
    payload: AddWorkflowProposalsDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/AddWorkflowProposals",
      payload
    );
  }

  uploadProjectFiles(
    fileObj: FileUploadDto[]
  ): Observable<GenericResponseDto<string>> {
    return this._http.post<GenericResponseDto<string>>(
      this.fileManagementBaseURL + "/upload",
      fileObj[0]
    );
  }

  saveProjectFile(
    payload: ProjectFileDto
  ): Observable<GenericResponseDto<number>> {
    return this._http.post<GenericResponseDto<number>>(
      this.productionWorkflowBaseURL + "/AddProjectWorkFile",
      payload
    );
  }

  getProjectFiles(
    workflowId: number
  ): Observable<GenericResponseDto<ProjectFileDto[]>> {
    return this._http.get<GenericResponseDto<ProjectFileDto[]>>(
      this.productionWorkflowBaseURL +
        `/GetProjectFiles?WorkflowId=${workflowId}`
    );
  }

  postDeleteProject(
    workflowId: number
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL +
        "/DeleteProjectWorkflowHeader?WorkflowId=" +
        workflowId,
      {}
    );
  }

  getNonAddedSalesOrderLines(
    workflowId: number,
    salesOrderId: string
  ): Observable<GenericResponseDto<NonAddedSalesOrderLineDto[]>> {
    return this._http.get<GenericResponseDto<NonAddedSalesOrderLineDto[]>>(
      this.productionWorkflowBaseURL +
        `/GetNonAddedSalesOrderLines?WorkflowId=${workflowId}&SalesOrderId=${salesOrderId}`
    );
  }

  postAddLineToProjectWorkflow(
    request: AddLinesToProjectWorkflowDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/AddProjectWorkflowLines",
      request
    );
  }

  addProjectWorkflowLineNotes(
    payload: AddProjectWorkflowLineNotesDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/AddProjectWorkflowLineNotes",
      payload
    );
  }

  updateProjectWorkflowSorting(
    request: UpdateProjectWorkflowSortingDto[]
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/UpdateProjectWorkflowSorting",
      request
    );
  }

  clearProjectWorkflowSorting(): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/ClearProjectWorkflowSorting",
      {}
    );
  }

  updateProjectWorkflowFlag(
    payload: UpdateProjectWorkflowFlagDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/UpdateProjectWorkflowFlag",
      payload
    );
  }

  updateProjectWorkflowIHD(
    request: UpdateProjectWorkflowIhdDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/UpdateProjectWorkflowIHD",
      request
    );
  }

  getManualTrackingShippingMethods(): Observable<
    GenericResponseDto<{ code: string; shippingMethod: string }[]>
  > {
    return this._http.get<
      GenericResponseDto<{ code: string; shippingMethod: string }[]>
    >(this.productionWorkflowBaseURL + `/GetManualShippingMethodList`);
  }

  updateManualTrackingStatus(
    request: UpdateManualTrackingStatusDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/UpdateManualTrackingStatus",
      request
    );
  }

  downloadProjectFile(url: string): Observable<Blob> {
    return this._http.get(url, { responseType: "blob" });
  }

  deleteProjectFile(
    fileId: number,
    workflowId: number
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/DeleteProjectFile",
      { fileId: fileId, workflowId: workflowId }
    );
  }

  deleteFileFromBlob(
    fileName: string
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.fileManagementBaseURL + `/delete?fileName=${fileName}`,
      {}
    );
  }

  projectSummaryExcelDownload(
    workflowId: number
  ): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      this.productionWorkflowBaseURL +
        `/ProductionWorkflowSummaryDocumentExcel?WorkflowId=${workflowId}`
    );
  }

  getTaskListByProjectWorkflowId(
    workflowId: number,
    taskType: number
  ): Observable<GenericResponseDto<GetAllTaskResponse[]>> {
    return this._http.get<GenericResponseDto<GetAllTaskResponse[]>>(
      this.taskManagementBaseURL +
        `/GetTaskListByProjectWorkflow?ProjectWorkflowId=${workflowId}&TaskType=${taskType}`
    );
  }

  getProjectHeaderList(): Observable<
    GenericResponseDto<PreSalesProjectWorkflowList[]>
  > {
    return this._http.get<GenericResponseDto<PreSalesProjectWorkflowList[]>>(
      this.productionWorkflowBaseURL + `/GetProjectHeaderList`
    );
  }

  renameProjectFile(
    req: RenameProjectFileRequestDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/RenameProjectFile",
      req
    );
  }

  setWorkflowUpdatePinStatus(
    request: WorkflowUpdatesPinRequestDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/SetProductionWorkflowUpdatePinStatus",
      request
    );
  }

  editProjectFileLink(
    payload: ProjectFileDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/EditProjectFileLink",
      payload
    );
  }

  MarkAsArchive(
    payload: MarkAsArchiveRequestDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + `/MarkAsArchive`,
      payload
    );
  }

  GetProjectActiveTasksCountByProjectId(
    workflowId: number
  ): Observable<GenericResponseDto<number>> {
    return this._http.get<GenericResponseDto<number>>(
      this.productionWorkflowBaseURL +
        `/GetProjectActiveTasksCountByProjectId?workflowId=${workflowId}`
    );
  }

  getProjectWorkFlowLinesTemplates(): Observable<
    GenericResponseDto<ProjectWorkFlowLinesTemplates[]>
  > {
    return this._http.get<GenericResponseDto<ProjectWorkFlowLinesTemplates[]>>(
      this.productionWorkflowBaseURL + `/GetProjectWorkFlowLinesTemplates`
    );
  }

  deleteProjectWorkFlowLinesTemplates(
    templateId: number
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + "/DeleteProjectWorkFlowLinesTemplates",
      templateId
    );
  }

  AddProjectWorkFlowLinesTemplates(
    payload: AddProjectWorkFlowLinesTemplatesRequestDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + `/AddProjectWorkFlowLinesTemplates`,
      payload
    );
  }
  getAllSalesOrdersListWithNoProjects(
    customerId: string
  ): Observable<GenericResponseDto<string[]>> {
    return this._http.get<GenericResponseDto<string[]>>(
      this.salesOrderManagmentBaseURL +
        `/GetAllSalesOrdersListWithNoProjects?customerIdD365=${customerId}`
    );
  }

  MultipleMarkAsArchive(
    payload: MarkAsArchiveRequestDto[]
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.productionWorkflowBaseURL + `/MultipleMarkAsArchive`,
      payload
    );
  }

  getAllProjectOwnersListed(): Observable<GenericResponseDto<string[]>> {
    return this._http.get<GenericResponseDto<string[]>>(
      this.productionWorkflowBaseURL + `/GetAllProjectOwnersListed`
    );
  }
}
